import { useCallback, useEffect, useMemo, useState } from "react";
import * as Tone from "tone"

type Config = {
    midiNotes: number[][]
    volume: number,
    bpm: number,
    playing: boolean,
    attack?: number,
    decay?: number,
    sustain?: number,
    release?: number
}

export default function useMusic({ playing, midiNotes, volume, bpm, attack = 0.01, decay = 0.1, sustain = 0.5, release = 1 }: Config) {
    const [currentChord, setCurrentChord] = useState<number|undefined>(0)

    const synth = useMemo(() => new Tone.PolySynth(Tone.Synth).toDestination(), [])

    const playChord = useCallback((notes: number[], time?: number) => {
        const chord = notes.map(midi => Tone.Midi(midi).toNote())
        synth.triggerAttackRelease(chord, '2n', time || Tone.now(), volume / 100)
    }, [synth, volume])

    useEffect(() => {
        if (playing) {
            Tone.Transport.start()
        } 
        else {
            Tone.Transport.stop();
        }
    }, [playing])

    useEffect(() => { synth.set({ envelope: { attack, decay, sustain, release } }) }, [attack, decay, sustain, release, synth])

    useEffect(() => {
        Tone.Transport.bpm.value = bpm
    }, [bpm])

    useEffect(() => {
        const loops = midiNotes.map((notes, i) => new Tone.Loop(time => {
            setCurrentChord(i)
            playChord(notes, time)
        }, Tone.Time('2n').toSeconds() * midiNotes.length).start(Tone.Time('2n').toSeconds() * i));

        return () => {
            loops.forEach(loop => loop.stop())
        }
    }, [midiNotes, playChord])

    return [currentChord, playChord] as const;
}